// studio/AssistantPanel.jsx — Assistant IA partagé (Tech/Product) : chat LLM réel + plan + fichiers.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ST, PNL, chip } from "@/internal/studio/theme";
import { HDR } from "@/internal/studio/LightShell";
import { Sparkles, History, Settings2, Paperclip, Mic, Send, CheckCircle2, FileCode2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const STEP_COLORS = ["#38BDF8", "#F59E0B", "#A78BFA", "#F472B6", "#2EE27B", "#22D3EE"];

export default function AssistantPanel({ studio, subtitle, tabs, seed, quickActions, userInitial }) {
  const KEY = `aya_${studio}_chat`;
  const [tab, setTab] = useState(tabs[0]);
  const [msgs, setMsgs] = useState(() => { try { const s = JSON.parse(localStorage.getItem(KEY) || "null"); return Array.isArray(s) && s.length ? s : seed; } catch { return seed; } });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(msgs.slice(-12))); if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, KEY]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setMsgs((m) => [...m, { role: "user", text: message }]);
    setInput(""); setBusy(true);
    try {
      const r = await fetch(`${API}/studio/assist`, { method: "POST", headers: HDR(), body: JSON.stringify({ studio, message }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.message || d.error);
      setMsgs((m) => [...m, { role: "aya", text: d.reply, plan: d.plan, files: d.files }]);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const lastFiles = [...msgs].reverse().find((m) => m.files?.length)?.files || [];

  return (
    <section style={{ ...PNL, flex: 1 }} data-testid={`${studio}-assistant-panel`}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 13px 8px", flexShrink: 0 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#1DB954,#0E7A38)", display: "grid", placeItems: "center" }}><Sparkles size={16} color="#fff" /></div>
        <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 900 }}>AYA</div><div style={{ fontSize: 10, color: ST.muted }}>{subtitle}</div></div>
        <History size={14} color={ST.muted} /><Settings2 size={14} color={ST.muted} />
      </div>
      <div style={{ display: "flex", gap: 6, padding: "0 13px 10px", flexShrink: 0, flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "5px 13px", borderRadius: 999, border: `1px solid ${tab === t ? "rgba(29,185,84,0.5)" : ST.border2}`, background: tab === t ? "linear-gradient(180deg,#2ECC6B,#1DB954)" : "transparent", color: tab === t ? "#fff" : ST.muted, fontSize: 11, fontWeight: 800, cursor: "pointer" }}>{t}</button>
        ))}
      </div>
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "0 13px" }}>
        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#334155,#64748B)", display: "grid", placeItems: "center", fontSize: 10.5, fontWeight: 900, color: "#fff", flexShrink: 0 }}>{userInitial}</div>
            <div style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: "4px 12px 12px 12px", padding: "9px 12px", fontSize: 12, lineHeight: 1.5 }}>{m.text}</div>
          </div>
        ) : (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1DB954,#0E7A38)", display: "grid", placeItems: "center", flexShrink: 0 }}><Sparkles size={12} color="#fff" /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ background: ST.soft, border: `1px solid ${ST.border2}`, borderRadius: "4px 12px 12px 12px", padding: "9px 12px", fontSize: 12, lineHeight: 1.55 }}>{m.text}</div>
              {m.plan?.length > 0 && (
                <div style={{ marginTop: 7, border: `1px solid ${ST.border2}`, borderRadius: 12, overflow: "hidden" }}>
                  {m.plan.map((s, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 11px", borderBottom: j < m.plan.length - 1 ? `1px solid ${ST.border2}` : "none", animation: `studioFade 0.35s ease ${j * 0.1}s both` }}>
                      <div style={{ width: 22, height: 22, borderRadius: 7, background: `${STEP_COLORS[j % 6]}22`, color: STEP_COLORS[j % 6], display: "grid", placeItems: "center", fontSize: 10.5, fontWeight: 900, flexShrink: 0 }}>{j + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 11.5, fontWeight: 800 }}>{s.step}</div>{s.detail && <div style={{ fontSize: 10, color: ST.muted }}>{s.detail}</div>}</div>
                      <CheckCircle2 size={13} color={ST.green2} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div style={{ fontSize: 11.5, color: ST.muted, padding: "4px 0 10px 34px" }}>AYA réfléchit…</div>}
        {lastFiles.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10.5, fontWeight: 900, color: ST.muted, marginBottom: 5 }}>Fichiers qui seront créés/modifiés :</div>
            {lastFiles.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", border: `1px solid ${ST.border2}`, borderRadius: 9, marginBottom: 4, background: ST.card }}>
                <FileCode2 size={12} color={ST.blue} />
                <span style={{ flex: 1, fontSize: 10.5, fontFamily: ST.mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.path}</span>
                <span style={chip(f.action === "créer" ? ST.green2 : ST.gold)}>{f.action === "créer" ? "Nouveau" : "À modifier"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ padding: "9px 13px 11px", borderTop: `1px solid ${ST.border2}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: ST.soft, border: `1px solid ${ST.border2}`, borderRadius: 999, padding: "7px 8px 7px 13px" }}>
          <Paperclip size={13} color={ST.muted} />
          <input data-testid={`${studio}-assist-input`} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Écrivez à AYA…" style={{ flex: 1, background: "none", border: "none", outline: "none", color: ST.text, fontSize: 12, fontFamily: "inherit" }} />
          <Mic size={13} color={ST.muted} />
          <button data-testid={`${studio}-assist-send`} onClick={() => send()} disabled={busy} style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "linear-gradient(135deg,#2ECC6B,#1DB954)", display: "grid", placeItems: "center", cursor: "pointer", opacity: busy ? 0.5 : 1 }}><Send size={13} color="#fff" /></button>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {quickActions.map(([icon, l, prompt]) => (
            <button key={l} onClick={() => send(prompt)} style={{ padding: "5px 11px", borderRadius: 999, border: `1px solid ${ST.border2}`, background: "transparent", color: ST.muted, fontSize: 10.5, fontWeight: 800, cursor: "pointer" }}>{icon} {l}</button>
          ))}
        </div>
      </div>
    </section>
  );
}
